import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Shield, Eye, Database, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';

const Privacy: React.FC = () => {
  const sections = [
    {
      title: 'Information vi indsamler',
      icon: <Database className="h-6 w-6" />,
      content: `Vi indsamler information, som du giver direkte til os, herunder:
        • Personlige oplysninger (navn, e-mail, telefonnummer)
        • Kontooplysninger og præferencer
        • Transaktions- og betalingsoplysninger
        • Kommunikationshistorik
        • Enhed og brugsoplysninger
        Vi indsamler også information automatisk, når du bruger vores platform, herunder enhedsoplysninger, IP-adresse og cookies.`
    },
    {
      title: 'Hvordan vi bruger dine oplysninger',
      icon: <Eye className="h-6 w-6" />,
      content: `Vi bruger dine oplysninger til at:
        • Levere og forbedre vores tjenester
        • Behandle transaktioner og sende notifikationer
        • Verificere identitet og forhindre svindel
        • Kommunikere med dig om vores tjenester
        • Analysere platformbrug og tendenser
        • Overholde juridiske forpligtelser`
    },
    {
      title: 'Deling af oplysninger',
      icon: <MessageSquare className="h-6 w-6" />,
      content: `Vi deler dine oplysninger med:
        • Andre brugere som nødvendigt for transaktioner
        • Tjenesteudbydere og forretningspartnere
        • Retshåndhævelse når påkrævet
        • Tredjeparter med dit samtykke
        Vi sælger aldrig dine personlige oplysninger til annoncører eller andre tredjeparter.`
    },
    {
      title: 'Datasikkerhed',
      icon: <Shield className="h-6 w-6" />,
      content: `Vi implementerer passende tekniske og organisatoriske foranstaltninger for at beskytte dine oplysninger, herunder:
        • Kryptering af følsomme data
        • Regelmæssige sikkerhedsvurderinger
        • Adgangskontrol og overvågning
        • Sikker datalagring og transmission
        • Medarbejdertræning om databeskyttelse`
    },
    {
      title: 'Dine rettigheder og valg',
      content: `Du har ret til at:
        • Få adgang til dine personlige oplysninger
        • Rette ukorrekte oplysninger
        • Anmode om sletning af dine oplysninger
        • Fravælge marketingkommunikation
        • Kontrollere cookie-præferencer
        Kontakt os for at udøve disse rettigheder.`
    },
    {
      title: 'Dataopbevaring',
      content: `Vi opbevarer dine oplysninger så længe, det er nødvendigt for at:
        • Levere vores tjenester
        • Overholde juridiske forpligtelser
        • Løse tvister
        • Håndhæve vores aftaler
        Du kan til enhver tid anmode om sletning af din konto og tilknyttede data.`
    },
    {
      title: 'Børns privatliv',
      content: `Vores tjenester er ikke rettet mod børn under 13 år. Vi indsamler ikke bevidst oplysninger fra børn under 13 år. Hvis du mener, at vi har indsamlet oplysninger fra et barn under 13 år, bedes du kontakte os straks.`
    },
    {
      title: 'Internationale dataoverførsler',
      content: `Vi kan overføre dine oplysninger til andre lande end dit bopælsland. Vi sikrer, at der er passende beskyttelsesforanstaltninger på plads for disse overførsler gennem standardkontraktbestemmelser og andre juridiske mekanismer.`
    },
    {
      title: 'Ændringer til denne politik',
      content: `Vi kan opdatere denne privatlivspolitik fra tid til anden. Vi vil give dig besked om væsentlige ændringer gennem vores platform eller via e-mail. Din fortsatte brug af vores tjenester efter sådanne ændringer udgør accept af den opdaterede politik.`
    },
    {
      title: 'Kontakt os',
      content: `Hvis du har spørgsmål om denne privatlivspolitik eller vores privatlivspraksis, bedes du kontakte vores databeskyttelsesansvarlige på privacy@do-more.site eller gennem vores hjælpecenter.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <Lock className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privatlivspolitik</h1>
          <p className="text-xl text-gray-600">
            Sidst opdateret: 11. februar 2026
          </p>
        </div>

        {/* Introduction */}
        <div className="max-w-3xl mx-auto mb-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-gray-600">
              Hos DoMore tager vi dit privatliv alvorligt. Denne privatlivspolitik forklarer, hvordan vi indsamler, 
              bruger, videregiver og beskytter dine oplysninger, når du bruger vores platform. Læs venligst 
              denne politik omhyggeligt for at forstå vores praksis vedrørende dine personlige data.
            </p>
          </div>
        </div>

        {/* Privacy Sections */}
        <div className="max-w-3xl mx-auto">
          {sections.map((section, index) => (
            <div key={index} className="mb-12">
              <div className="flex items-center mb-4">
                {section.icon && (
                  <div className="text-green-600 mr-3">{section.icon}</div>
                )}
                <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="prose max-w-none">
                  <p className="text-gray-600 whitespace-pre-line">{section.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="max-w-3xl mx-auto mt-16">
          <div className="bg-green-600 rounded-lg shadow-lg text-white p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Spørgsmål om privatliv?</h2>
            <p className="text-green-100 mb-8">
              Hvis du har spørgsmål om vores privatlivspraksis eller ønsker at udøve 
              dine privatlivsrettigheder, bedes du kontakte os.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <button style={{height: '40px'}} className="inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 px-4 py-2 text-sm bg-white text-green-600 hover:bg-green-50">
                  Kontakt os
                </button>
              </Link>
              <Link to="/help">
                <button className="inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white px-4 py-2 text-sm border-2 border-white text-white hover:bg-white hover:text-green-600">
                  Besøg hjælpecenter
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Related Links */}
        <div className="max-w-3xl mx-auto mt-12">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-600">
            <Link to="/terms" className="hover:text-green-600">
              Servicevilkår
            </Link>
            <span className="hidden sm:inline text-gray-300">|</span>
            <Link to="/safety" className="hover:text-green-600">
              Sikkerhedsretningslinjer
            </Link>
            <span className="hidden sm:inline text-gray-300">|</span>
            <Link to="/help" className="hover:text-green-600">
              Hjælpecenter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;